import type { AnalyticsComparison, LensValueFormat } from '~/types/leLens';

import type { CommercialPeriod } from '../commercial/analyticsState';

export const LOYALTY_PROGRAMS = ['societe', 'luxplus'] as const;
export type LoyaltyProgram = (typeof LOYALTY_PROGRAMS)[number];

export const LOYALTY_PERIODS = [
  'yesterday',
  'week_to_date',
  'month_to_date',
  'quarter_to_date',
  'year_to_date',
  'last_7_days',
  'last_14_days',
  'last_28_days',
  'last_30_days',
  'last_90_days',
  'last_12_months',
  'custom',
] as const satisfies ReadonlyArray<CommercialPeriod>;
export type LoyaltyPeriod = (typeof LOYALTY_PERIODS)[number];

export const LOYALTY_COMPARISONS = [
  'previous_period',
  'previous_year',
  'custom',
  'none',
] as const satisfies ReadonlyArray<AnalyticsComparison>;
export type LoyaltyComparison = (typeof LOYALTY_COMPARISONS)[number];

export const LOYALTY_TREND_GRAINS = ['day', 'week', 'month', 'quarter'] as const;
export type LoyaltyTrendGrain = (typeof LOYALTY_TREND_GRAINS)[number];

export const LOYALTY_TREND_VIEWS = ['trend', 'cumulative', 'change'] as const;
export type LoyaltyTrendView = (typeof LOYALTY_TREND_VIEWS)[number];

export const LOYALTY_FILTER_KEYS = ['tier', 'country', 'brand', 'category', 'autoRenew', 'promo'] as const;
export type LoyaltyFilterKey = (typeof LOYALTY_FILTER_KEYS)[number];
export type LoyaltyFilters = Partial<Record<LoyaltyFilterKey, string[]>>;

/**
 * Backend filter id per programme. Country is one reader-facing key that resolves to the
 * programme's own column, so it never carries a Société value into a LuxPlus+ request.
 */
export const LOYALTY_FILTER_FIELDS: Record<LoyaltyProgram, Partial<Record<LoyaltyFilterKey, string>>> = {
  societe: { tier: 'tier', country: 'country_name', brand: 'brand_name', category: 'category' },
  luxplus: {
    country: 'parent_membership_country',
    autoRenew: 'parent_auto_renewal_status',
    promo: 'parent_sub_is_promo',
  },
};

export type LoyaltyViewState = {
  /** True while the Explore panel under the strip is open. Closed by default so the page stays uncluttered. */
  explore?: boolean;
  program: LoyaltyProgram;
  period: LoyaltyPeriod;
  from?: string;
  to?: string;
  comparison: LoyaltyComparison;
  comparisonFrom?: string;
  comparisonTo?: string;
  trendGrain: LoyaltyTrendGrain;
  focusMetric?: string;
  breakdown?: string;
  /** Extra breakdown columns, as metric ids in the order chosen. */
  columns?: string[];
  trendView: LoyaltyTrendView;
  filters?: LoyaltyFilters;
};

/** One governed Loyalty KPI as the snapshot's `metrics[]` returns it. Programmes never mix. */
export type LoyaltyMetric = {
  id: string;
  label: string;
  definition: string;
  programme: LoyaltyProgram;
  format: LensValueFormat;
  favourableDirection: 'up' | 'down' | 'contextual';
  state: 'draft' | 'certified';
  value: number | null;
  comparisonValue: number | null;
  absoluteChange: number | null;
  /** Unit fraction, 0.1 = 10%. */
  percentChange: number | null;
  source: { catalogueId: string; field?: string; measureId?: string; lensUnavailableReason?: string };
  /** Renewal rate only: false while the window still holds unclassified renewal outcomes. */
  settled?: boolean;
  /** Which strip the metric belongs to. Absent on servers that predate the field. */
  group?: 'primary' | 'supporting';
};
